import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Avatar,
  Divider,
  Stack,
  Checkbox,
  IconButton,
  TextField,
  CircularProgress,
  Button,
  Chip,
  Drawer,
  Paper,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Search as SearchIcon,
  BusinessCenter as BusinessIcon,
  LocalHospital as HealthIcon,
  ShoppingCart as ShoppingCartIcon,
  Cloud as CloudIcon,
  School as SchoolIcon,
  LocationOn as LocationIcon,
  AccessTime as AccessTimeIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  Tune as TuneIcon,
  People as PeopleIcon,
} from "@mui/icons-material";

import TopBar from "./globalComponents/topbar";
import Footer from "./globalComponents/footer";

const API = "https://backend-global-1vq9.onrender.com";

// ── Helper: build a full URL from a server-stored image path ─────────────────
// Same pattern used everywhere else — handles relative paths from old local
// uploads and full Cloudinary URLs from new ones without any extra logic.
const resolveImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http") || path.startsWith("data:") || path.startsWith("blob:")) return path;
  return `${API}/${String(path).replace(/\\/g, "/").replace(/^\//, "")}`;
};

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
};

// ─── Horizontal Mentor Row Card ───────────────────────────────────────────────
function MentorCard({ mentor, onConnect }) {
  const initials = (mentor.fullName || "")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  // profileImage lives on the User model — the mentor list endpoint may carry
  // it directly on the mentor record, or only on the populated userId object.
  // Check both so this works regardless of which shape the backend returns.
  const avatarUrl = resolveImageUrl(mentor.profileImage || mentor.userId?.profileImage);

  const expertiseList = mentor.areasOfExpertise?.slice(0, 4) || [];

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: C.paper,
        borderRadius: 3,
        border: `1px solid ${C.border}`,
        overflow: "hidden",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        transition: "all 0.22s ease",
        "&:hover": {
          boxShadow: `0 6px 24px ${C.primary}15`,
          transform: "translateY(-1px)",
          borderColor: C.primary,
        },
      }}
    >
      {/* Left accent strip — brand gradient */}
      <Box
        sx={{
          width: { xs: "100%", sm: 5 },
          height: { xs: 5, sm: "auto" },
          minHeight: { sm: "100%" },
          background: `linear-gradient(180deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
          flexShrink: 0,
          alignSelf: "stretch",
        }}
      />

      {/* Avatar section */}
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: { xs: 2, sm: 2 },
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Avatar
          src={avatarUrl}
          alt={mentor.fullName || "Mentor"}
          sx={{
            width: { xs: 58, sm: 68 },
            height: { xs: 58, sm: 68 },
            background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
            color: "#fff",
            fontSize: { xs: 22, sm: 26 },
            fontWeight: 800,
            letterSpacing: "-0.5px",
            boxShadow: `0 4px 12px ${C.primary}30`,
          }}
        >
          {!avatarUrl && initials}
        </Avatar>
      </Box>

      {/* Main info — grows to fill */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          py: { xs: 0, sm: 2 },
          px: { xs: 2, sm: 0 },
          pb: { xs: 1, sm: 2 },
        }}
      >
        <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap">
          <Typography
            sx={{ fontSize: { xs: 15, sm: 17 }, color: C.textPrimary, lineHeight: 1.3, fontWeight: 800, letterSpacing: -0.2 }}
          >
            {mentor.fullName}
          </Typography>
        </Stack>

        <Typography
          sx={{
            color: C.primary,
            fontWeight: 700,
            fontSize: { xs: 12, sm: 13 },
            mt: 0.25,
            mb: 1,
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {mentor.professionalBackground}
        </Typography>

        {/* Meta chips row */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          mb={1}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <LocationIcon sx={{ fontSize: 13, color: C.textMuted }} />
            <Typography sx={{ fontSize: 12, color: C.textSecondary, fontWeight: 600 }}>
              {mentor.location || "N/A"}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <AccessTimeIcon sx={{ fontSize: 13, color: C.textMuted }} />
            <Typography sx={{ fontSize: 12, color: C.textSecondary, fontWeight: 600 }}>
              {mentor.yearsOfExperience} yrs exp
            </Typography>
          </Stack>
        </Stack>

        {/* Expertise tags */}
        {expertiseList.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {expertiseList.map((tag, i) => (
              <Chip
                key={i}
                label={tag}
                size="small"
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: C.primarySoft,
                  color: C.primary,
                  border: `1px solid ${alpha(C.primary, 0.18)}`,
                  borderRadius: 1.5,
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            ))}
          </Stack>
        )}
      </Box>

      {/* Right: Connect button */}
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: { xs: 2, sm: 2 },
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Button
          onClick={() => onConnect(mentor)}
          variant="contained"
          endIcon={<ArrowForwardIcon sx={{ fontSize: 15 }} />}
          sx={{
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            color: "#fff",
            fontWeight: 800,
            fontSize: 13,
            borderRadius: 2.5,
            px: 2.5,
            py: 1,
            whiteSpace: "nowrap",
            boxShadow: "none",
            textTransform: "none",
            minWidth: { xs: "100%", sm: 130 },
            "&:hover": {
              boxShadow: `0 4px 14px ${C.primary}40`,
            },
          }}
        >
          Connect
        </Button>
      </Box>
    </Paper>
  );
}

// ─── Filter Panel ─────────────────────────────────────────────────────────────
function FilterPanel({ searchTerm, onSearch }) {
  const expertiseItems = [
    "Business Strategy",
    "Digital Marketing",
    "Product Development",
    "Fundraising & Investment",
    "UX/UI Design",
  ];
  const industryItems = [
    { label: "FinTech", icon: <BusinessIcon sx={{ fontSize: 16 }} /> },
    { label: "HealthTech", icon: <HealthIcon sx={{ fontSize: 16 }} /> },
    { label: "E-commerce", icon: <ShoppingCartIcon sx={{ fontSize: 16 }} /> },
    { label: "SaaS", icon: <CloudIcon sx={{ fontSize: 16 }} /> },
    { label: "EdTech", icon: <SchoolIcon sx={{ fontSize: 16 }} /> },
  ];

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2.5,
      fontSize: 13,
      bgcolor: C.primarySofter,
      "& fieldset": { borderColor: C.border },
      "&:hover fieldset": { borderColor: C.primary },
      "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: 2 },
    },
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 11, fontWeight: 800, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", mb: 1 }}>
        Search
      </Typography>
      <TextField
        placeholder="Search mentors..."
        size="small"
        variant="outlined"
        fullWidth
        value={searchTerm}
        onChange={(e) => onSearch(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ color: C.textMuted, mr: 1, fontSize: 17 }} />,
        }}
        sx={{ mb: 3, ...fieldSx }}
      />

      <Typography sx={{ fontSize: 11, fontWeight: 800, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", mb: 1 }}>
        Expertise
      </Typography>
      <Stack spacing={0.25} mb={3}>
        {expertiseItems.map((item, i) => (
          <Stack
            key={i}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 1, py: 0.5, borderRadius: 1.5,
              "&:hover": { bgcolor: C.primarySofter }, cursor: "pointer",
            }}
          >
            <Typography sx={{ color: C.textPrimary, fontSize: 13, fontWeight: 600 }}>{item}</Typography>
            <Checkbox
              size="small"
              sx={{
                p: 0.5,
                color: C.border,
                "&.Mui-checked": { color: C.primary },
              }}
            />
          </Stack>
        ))}
      </Stack>

      <Divider sx={{ mb: 3, borderColor: C.borderSoft }} />

      <Typography sx={{ fontSize: 11, fontWeight: 800, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", mb: 1 }}>
        Industry
      </Typography>
      <Stack spacing={0.25}>
        {industryItems.map((item, i) => (
          <Stack
            key={i}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 1, py: 0.5, borderRadius: 1.5,
              "&:hover": { bgcolor: C.primarySofter }, cursor: "pointer",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ color: C.primary, display: "flex" }}>{item.icon}</Box>
              <Typography sx={{ color: C.textPrimary, fontSize: 13, fontWeight: 600 }}>{item.label}</Typography>
            </Stack>
            <Checkbox
              size="small"
              sx={{
                p: 0.5,
                color: C.border,
                "&.Mui-checked": { color: C.primary },
              }}
            />
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function MentorList() {
  const [mentors, setMentors] = useState([]);
  const [filteredMentors, setFilteredMentors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const mentorsPerPage = 5;

  const navigate = useNavigate();

  useEffect(() => {
    const fetchMentors = async () => {
      try {
        setLoading(true);
        const res = await axios.get("https://backend-global-1vq9.onrender.com/api/mentor/list");
        setMentors(res.data.mentors);
        setFilteredMentors(res.data.mentors);
      } catch (err) {
        console.error("Error fetching mentors:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchMentors();
  }, []);

  useEffect(() => {
    const results = mentors.filter((m) =>
      m.fullName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredMentors(results);
    setCurrentPage(0);
  }, [searchTerm, mentors]);

  const totalPages = Math.ceil(filteredMentors.length / mentorsPerPage);
  const indexOfLast = (currentPage + 1) * mentorsPerPage;
  const indexOfFirst = indexOfLast - mentorsPerPage;
  const currentMentors = filteredMentors.slice(indexOfFirst, indexOfLast);

  const handleConnect = (mentor) => navigate(`/myrequests/${mentor.userId}`);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
      <TopBar />

      <Box sx={{ flex: 1, px: { xs: 2, sm: 3, md: 5, lg: 9 }, py: { xs: 3, md: 4 } }}>

        {/* ── Brand Hero Header ─────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            borderRadius: 3,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            p: { xs: 3, sm: 4 },
            mb: { xs: 2.5, sm: 3 },
            color: "#fff",
            boxShadow: `0 6px 24px ${C.primary}30`,
          }}
        >
          {/* Green orb decoration */}
          <Box sx={{
            position: "absolute",
            top: "-40%", right: "-10%",
            width: { xs: 200, sm: 280 }, height: { xs: 200, sm: 280 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />

          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1.25} mb={1}>
                  <PeopleIcon sx={{ fontSize: 20, color: "rgba(255,255,255,0.85)" }} />
                  <Typography sx={{
                    fontSize: { xs: 11, sm: 12 }, fontWeight: 700, letterSpacing: 1.5,
                    textTransform: "uppercase", color: "rgba(255,255,255,0.75)",
                  }}>
                    Mentor Directory
                  </Typography>
                </Stack>
                <Typography sx={{
                  fontWeight: 800, fontSize: { xs: 22, sm: 28, md: 32 },
                  color: "#fff", lineHeight: 1.15, mb: 0.75, letterSpacing: -0.5,
                }}>
                  Find a Mentor
                </Typography>
                <Typography sx={{
                  color: "rgba(255,255,255,0.85)",
                  fontSize: { xs: 13, sm: 14 }, maxWidth: 540, lineHeight: 1.5,
                }}>
                  Connect with experienced mentors across business, tech, health, and beyond. Send a request and start your conversation.
                </Typography>

                {!loading && mentors.length > 0 && (
                  <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={`${mentors.length} mentors available`}
                      size="small"
                      sx={{
                        bgcolor: "rgba(255,255,255,0.18)",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 11,
                      }}
                    />
                  </Stack>
                )}
              </Box>

              {isMobile && (
                <Button
                  variant="contained"
                  startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setDrawerOpen(true)}
                  sx={{
                    bgcolor: "#fff",
                    color: C.primary,
                    fontWeight: 800,
                    fontSize: 13,
                    textTransform: "none",
                    borderRadius: 2.5,
                    px: 2,
                    py: 1,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    flexShrink: 0,
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.92)",
                      boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                    },
                  }}
                >
                  Filters
                </Button>
              )}
            </Stack>
          </Box>
        </Paper>

        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">

          {/* Desktop sidebar */}
          {!isMobile && (
            <Paper
              elevation={0}
              sx={{
                width: 270,
                flexShrink: 0,
                bgcolor: C.paper,
                borderRadius: 3,
                border: `1px solid ${C.border}`,
                p: 2.5,
                position: "sticky",
                top: 24,
                boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0, left: 0, right: 0,
                  height: "3px",
                  background: `linear-gradient(90deg, ${C.primary}, ${C.primary}88)`,
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={2} mt={0.5}>
                <TuneIcon sx={{ fontSize: 18, color: C.primary }} />
                <Typography sx={{ fontSize: 15, color: C.textPrimary, fontWeight: 800, letterSpacing: -0.2 }}>
                  Browse Filters
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2.5, borderColor: C.borderSoft }} />
              <FilterPanel searchTerm={searchTerm} onSearch={setSearchTerm} />
            </Paper>
          )}

          {/* Mobile drawer */}
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{ sx: { width: 290, p: 3, bgcolor: C.paper } }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TuneIcon sx={{ fontSize: 18, color: C.primary }} />
                <Typography sx={{ fontWeight: 800, color: C.textPrimary, fontSize: 16 }}>
                  Browse Filters
                </Typography>
              </Stack>
              <IconButton onClick={() => setDrawerOpen(false)} size="small" sx={{ color: C.textMuted }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Divider sx={{ mb: 2.5, borderColor: C.borderSoft }} />
            <FilterPanel searchTerm={searchTerm} onSearch={setSearchTerm} />
          </Drawer>

          {/* Mentor list column */}
          <Box flex={1} minWidth={0}>
            {/* Sub-header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
              <Typography sx={{ fontWeight: 800, color: C.textPrimary, fontSize: 16, letterSpacing: -0.2 }}>
                Mentor Profiles
              </Typography>
              {!loading && filteredMentors.length > 0 && (
                <Typography sx={{ fontSize: 12, color: C.textMuted, fontWeight: 600 }}>
                  Showing {indexOfFirst + 1}–{Math.min(indexOfLast, filteredMentors.length)} of {filteredMentors.length}
                </Typography>
              )}
            </Stack>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
                <CircularProgress size={50} thickness={5} sx={{ color: C.primary }} />
              </Box>
            ) : currentMentors.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  minHeight: 280, py: 4,
                  bgcolor: C.paper,
                  borderRadius: 3,
                  border: `1px solid ${C.border}`,
                }}
              >
                <Box sx={{
                  width: 72, height: 72, borderRadius: "50%",
                  background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  mb: 2,
                  boxShadow: `0 8px 20px ${C.primary}30`,
                }}>
                  <SearchIcon sx={{ fontSize: 32, color: "#fff" }} />
                </Box>
                <Typography sx={{ fontWeight: 800, color: C.textPrimary, mb: 0.5, fontSize: 15 }}>
                  No mentors found
                </Typography>
                <Typography sx={{ fontSize: 13, color: C.textSecondary }}>
                  Try a different search term
                </Typography>
              </Paper>
            ) : (
              <>
                {/* Vertical list */}
                <Stack spacing={2}>
                  {currentMentors.map((mentor) => (
                    <MentorCard
                      key={mentor.userId?._id || mentor._id}
                      mentor={mentor}
                      onConnect={handleConnect}
                    />
                  ))}
                </Stack>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    mt={4}
                    px={{ xs: 0, sm: 1 }}
                    flexWrap="wrap"
                    gap={2}
                  >
                    <Button
                      variant="outlined"
                      disabled={currentPage === 0}
                      onClick={() => setCurrentPage((p) => p - 1)}
                      sx={{
                        borderRadius: 2.5,
                        borderColor: C.primary,
                        color: C.primary,
                        fontWeight: 700,
                        fontSize: 13,
                        textTransform: "none",
                        px: 2.5,
                        "&:hover": {
                          borderColor: C.primaryDark,
                          bgcolor: C.primarySoft,
                        },
                        "&.Mui-disabled": {
                          borderColor: C.border,
                          color: C.textMuted,
                        },
                      }}
                    >
                      ← Previous
                    </Button>

                    {/* Dot indicators */}
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {Array.from({ length: totalPages }, (_, i) => (
                        <Box
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          sx={{
                            width: i === currentPage ? 24 : 8,
                            height: 8,
                            borderRadius: 1,
                            bgcolor: i === currentPage ? C.primary : C.border,
                            cursor: "pointer",
                            transition: "all 0.22s ease",
                            "&:hover": {
                              bgcolor: i === currentPage ? C.primary : C.textMuted,
                            },
                          }}
                        />
                      ))}
                    </Stack>

                    <Button
                      variant="outlined"
                      disabled={currentPage >= totalPages - 1}
                      onClick={() => setCurrentPage((p) => p + 1)}
                      sx={{
                        borderRadius: 2.5,
                        borderColor: C.primary,
                        color: C.primary,
                        fontWeight: 700,
                        fontSize: 13,
                        textTransform: "none",
                        px: 2.5,
                        "&:hover": {
                          borderColor: C.primaryDark,
                          bgcolor: C.primarySoft,
                        },
                        "&.Mui-disabled": {
                          borderColor: C.border,
                          color: C.textMuted,
                        },
                      }}
                    >
                      Next →
                    </Button>
                  </Stack>
                )}
              </>
            )}
          </Box>
        </Stack>
      </Box>

      <Footer />
    </Box>
  );
}